import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  RESOURCE_LIST_FETCH_STALE_MS,
  queryKeys,
} from '@cotera/client/app/hooks/query-cache-keys';
import type { FolderItemsResponse } from '@cotera/client/app/etc/api/typed-client';
import { favoriteKey, type FavoriteRef } from './favorite-key';

/** One favorited resource, in the item shape every Library display helper reads. */
export type FavoriteItem = FolderItemsResponse['items'][number];

const EMPTY_ITEMS: FavoriteItem[] = [];

/** The (type, id) a favorites item points at. */
export const favoriteRefOfItem = (item: FavoriteItem): FavoriteRef => ({
  // The item union's discriminant is the `ResourceType` value itself
  // (`workflow_trigger` and friends), so this is a widening, not a mapping.
  type: item.type as ResourceType,
  id: item.data.id,
});

export type FavoritesState = {
  items: FavoriteItem[];
  isPending: boolean;
  isFavorite: (type: ResourceType, id: string) => boolean;
};

/**
 * The user's favorites.
 *
 * A plain React Query read — the cache is the shared state, so the four surfaces
 * that read a favorite (sidebar list, page-header star, Library row marker, row
 * menu) get one fetch and one source of truth without a provider. Writing a
 * favorite is `ToggleFavoriteAction`'s job alone; reordering is
 * {@link useReorderFavorites}. Both keep this cache honest by invalidating it.
 */
export function useFavorites(): FavoritesState {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data, isPending } = useQuery({
    queryKey: queryKeys.resource.favorites({ orgId }),
    staleTime: RESOURCE_LIST_FETCH_STALE_MS,
    queryFn: async () =>
      Assert.assertOk(await client.resource.favorites.list()),
  });

  const items = data?.items ?? EMPTY_ITEMS;
  const isFavorite = useCallback(
    (type: ResourceType, id: string): boolean =>
      items.some((item) => {
        const ref = favoriteRefOfItem(item);
        return ref.type === type && ref.id === id;
      }),
    [items]
  );

  return { items, isPending, isFavorite };
}

/** Whether one resource is starred. The narrow read most call sites want. */
export function useIsFavorite(type: ResourceType, id: string): boolean {
  return useFavorites().isFavorite(type, id);
}

/**
 * Commit a new favorites order, optimistically.
 *
 * Reorder only moves rows that already exist, so the new order can be written
 * straight into the query cache and rolled back on failure — no overlay needed.
 */
export function useReorderFavorites(): (order: FavoriteRef[]) => void {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const queryClient = useQueryClient();
  const queryKey = queryKeys.resource.favorites({ orgId });

  const { mutate } = useMutation({
    mutationFn: async (order: FavoriteRef[]) =>
      Assert.assertOk(await client.resource.favorites.reorder({ order })),
    onMutate: async (order: FavoriteRef[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<FolderItemsResponse>(queryKey);
      queryClient.setQueryData<FolderItemsResponse>(queryKey, (current) =>
        current === undefined ? current : reorderItems(current, order)
      );
      return { previous };
    },
    onError: (_error, _order, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData<FolderItemsResponse>(
          queryKey,
          context.previous
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return useCallback((order: FavoriteRef[]) => mutate(order), [mutate]);
}

function reorderItems(
  data: FolderItemsResponse,
  order: FavoriteRef[]
): FolderItemsResponse {
  const rank = new Map(
    order.map((ref, index) => [favoriteKey(ref.type, ref.id), index])
  );
  const rankOf = (item: FavoriteItem): number => {
    const ref = favoriteRefOfItem(item);
    return rank.get(favoriteKey(ref.type, ref.id)) ?? Number.MAX_SAFE_INTEGER;
  };
  return {
    ...data,
    items: [...data.items].sort((a, b) => rankOf(a) - rankOf(b)),
  };
}
